import {
  PhoneAuthProvider,
  multiFactor,
  RecaptchaVerifier,
  PhoneMultiFactorGenerator,
  MultiFactorResolver,
  MultiFactorError,
  getMultiFactorResolver,
  applyActionCode,
  User,
} from "firebase/auth";
import { useAtom } from "jotai";
import { useCallback, useRef } from "react";


import { useAppSnackbar } from "@/custom-hooks/use-app-snackbar";
import { firebaseGetAuth } from "@/global-state/firebase-settings";
import { loadingAtom } from "@/global-state/jotai-atom";

import { useStaffApi } from "./apis/use-staff-api";

const mapFirebaseError = (code: string): string => {
  switch (code) {
    case "auth/second-factor-already-in-use":
      return "この電話番号は既に使用されています。";
    case "auth/invalid-verification-code":
      return "認証コードが無効です。";
    case "auth/code-expired":
      return "認証コードの有効期限が切れています。";
    case "auth/captcha-check-failed":
    case "auth/invalid-app-credential":
    case "auth/missing-app-credential":
      return "セキュリティ認証に失敗しました。ページをリロードして再試行してください。";
    default:
      return "エラーが発生しました。再度やり直してください。";
  }
};

export const useMultiFactor = () => {
  const { setAppSnackbar } = useAppSnackbar();
  const { sendEmailVerification } = useStaffApi();

  const [, setLoading] = useAtom(loadingAtom);

  const recaptchaRef = useRef<RecaptchaVerifier | null>(null);

  const ensureFirebaseUser = useCallback(async (): Promise<User | null> => {
    let user = firebaseGetAuth.currentUser;
    if (user) return user;

    return new Promise((resolve) => {
      const unsubscribe = firebaseGetAuth.onAuthStateChanged((u) => {
        unsubscribe();
        resolve(u);
      });
    });
  }, []);

  const getRecaptcha = (): RecaptchaVerifier => {
    if (!recaptchaRef.current) {
      recaptchaRef.current = new RecaptchaVerifier("recaptcha-container", { size: "invisible" }, firebaseGetAuth);
      recaptchaRef.current.render();
    }
    return recaptchaRef.current;
  };

  const checkRequiresRecentLogin = (code?: string): boolean => {
    if (code === "auth/requires-recent-login") {
      setAppSnackbar("この操作には再認証が必要です。パスワードを再入力してください。", { info: true });
      return true;
    }
    return false;
  };

  const checkUserEnrolled = useCallback((): boolean => {
    const user = firebaseGetAuth.currentUser;
    if (!user) {
      setAppSnackbar("ログイン状態が無効です。", { error: true });
      return false;
    }
    return !!multiFactor(user).enrolledFactors.find((f) => f.factorId === "phone");
  }, []);

  const sendCode = useCallback(async (phoneNumber: string): Promise<string | null> => {
    try {
      setLoading(true);
      const user = await ensureFirebaseUser();
      if (!user) throw new Error("ログイン状態が無効です。");

      if (!user.emailVerified) {
        await sendEmailVerification();
        setAppSnackbar("メール認証が必要です。メールをご確認ください。", { info: true });
        return null;
      }

      const session = await multiFactor(user).getSession();
      const recaptcha = getRecaptcha();

      const id = await new PhoneAuthProvider(firebaseGetAuth).verifyPhoneNumber({ phoneNumber, session }, recaptcha);

      setAppSnackbar("認証コードを送信しました。SMSをご確認ください。", { success: true });
      return id;
    } catch (e: any) {
      if (checkRequiresRecentLogin(e?.code)) return e?.code;
      setAppSnackbar(mapFirebaseError(e?.code || e?.message), { error: true });
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const enrollUser = useCallback(async (verificationId: string, code: string): Promise<boolean | string> => {
    try {
      const user = await ensureFirebaseUser();
      if (!user) throw new Error("ログイン状態が無効です。");

      const cred = PhoneAuthProvider.credential(verificationId, code);
      const assertion = PhoneMultiFactorGenerator.assertion(cred);

      await multiFactor(user).enroll(assertion, "Two factor phone number");
      setAppSnackbar("2段階認証を有効にしました。", { success: true });
      return true;
    } catch (e: any) {
      if (checkRequiresRecentLogin(e?.code)) return e?.code;
      setAppSnackbar(mapFirebaseError(e?.code || e?.message), { error: true });
      return false;
    }
  }, []);

  const unenrollUser = useCallback(async (): Promise<boolean | string> => {
    try {
      setLoading(true);
      const user = await ensureFirebaseUser();
      if (!user) throw new Error("ログイン状態が無効です。");

      const factor = multiFactor(user).enrolledFactors.find((f) => f.factorId === "phone");
      if (factor) await multiFactor(user).unenroll(factor.uid);

      setAppSnackbar("2段階認証を無効にしました。", { success: true });
      return true;
    } catch (e: any) {
      if (checkRequiresRecentLogin(e?.code)) return e?.code;
      setAppSnackbar(mapFirebaseError(e?.code || e?.message), { error: true });
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const sendUserMFA = useCallback(
    async (error: MultiFactorError): Promise<{ resolver: MultiFactorResolver; verificationId: string } | null> => {
      try {
        setLoading(true);
        const resolver = getMultiFactorResolver(firebaseGetAuth, error);
        const phoneInfo = resolver.hints[0];
        const recaptcha = getRecaptcha();

        const verificationId = await new PhoneAuthProvider(firebaseGetAuth).verifyPhoneNumber(
          { multiFactorHint: phoneInfo, session: resolver.session },
          recaptcha,
        );

        return { resolver, verificationId };
      } catch (e: any) {
        setAppSnackbar(mapFirebaseError(e?.code || e?.message), { error: true });
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const verifyEmailByOobCode = useCallback(async (oobCode: string): Promise<boolean> => {
    try {
      setLoading(true);
      const user = await ensureFirebaseUser();
      if (!user) {
        setAppSnackbar("ログイン状態が無効です。", { error: true });
        return false;
      }

      await applyActionCode(firebaseGetAuth, oobCode);
      await user.reload();
      await user.getIdToken(true);
      setAppSnackbar("メールアドレスが正常に確認されました。", { success: true });
      return true;
    } catch (error: any) {
      const code = error?.code;

      switch (code) {
        case "auth/expired-action-code":
        case "auth/invalid-action-code":
          setAppSnackbar(
            "認証リンクの有効期限が切れているか、誤っている可能性がございます。もう一度やり直してください。",
            { error: true },
          );
          break;
        case "auth/user-disabled":
          setAppSnackbar("このアカウントは無効化されています。", { error: true });
          break;
        default:
          setAppSnackbar("メール認証に失敗しました。", { error: true });
          break;
      }

      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    ensureFirebaseUser,
    checkUserEnrolled,
    sendCode,
    enrollUser,
    unenrollUser,
    sendUserMFA,
    verifyEmailByOobCode,
  };
};
